import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Assets.css';

// TypeScript interfaces
interface AssetAllocation {
  symbol: string;
  percentage: number;
  color: string;
}

// Mock data for asset distribution
const assetDistribution: AssetAllocation[] = [
  { symbol: 'USDT', percentage: 84.86, color: '#C9A962' },
  { symbol: 'ETH', percentage: 10.82, color: '#F7931A' },
  { symbol: 'BTC', percentage: 4.32, color: '#E84142' }
];

const Assets: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'spot' | 'futures' | 'earn'>('overview');
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [activeNav, setActiveNav] = useState<string>('assets');
  const [tradeModalOpen, setTradeModalOpen] = useState(false);

  // Calculate donut chart segments
  const calculateDonutSegments = () => {
    const circumference = 2 * Math.PI * 45; // radius = 45
    let offset = 0;

    return assetDistribution.map((asset) => {
      const segmentLength = (asset.percentage / 100) * circumference;
      const segment = {
        ...asset,
        strokeDasharray: `${segmentLength} ${circumference - segmentLength}`,
        strokeDashoffset: -offset
      };
      offset += segmentLength;
      return segment;
    });
  };

  const donutSegments = calculateDonutSegments();

  return (
    <div className="assets-container">
      {/* Filter Tabs */}
      <div className="assets-tabs-wrapper">
        <div className="assets-tabs">
          <button
            className={`assets-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`assets-tab ${activeTab === 'spot' ? 'active' : ''}`}
            onClick={() => setActiveTab('spot')}
          >
            Spot
          </button>
          <button
            className={`assets-tab ${activeTab === 'futures' ? 'active' : ''}`}
            onClick={() => setActiveTab('futures')}
          >
            Futures
          </button>
          <button
            className={`assets-tab ${activeTab === 'earn' ? 'active' : ''}`}
            onClick={() => setActiveTab('earn')}
          >
            Earn
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="assets-content">
        {/* Balance Card */}
        <div className="assets-balance-card">
          <button className="assets-balance-label">
            <span>TOTAL BALANCE</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <div className="assets-balance-row">
            <span className="assets-balance-amount">
              {balanceVisible ? '$0.00' : '••••••'}
            </span>
            <button
              className="assets-visibility-toggle"
              onClick={() => setBalanceVisible(!balanceVisible)}
              aria-label={balanceVisible ? 'Hide balance' : 'Show balance'}
            >
              {balanceVisible ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M1.33334 8.00001C1.33334 8.00001 3.33334 3.33334 8.00001 3.33334C12.6667 3.33334 14.6667 8.00001 14.6667 8.00001C14.6667 8.00001 12.6667 12.6667 8.00001 12.6667C3.33334 12.6667 1.33334 8.00001 1.33334 8.00001Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M9.41334 9.41334C9.23024 9.60983 9.00944 9.76744 8.76411 9.87686C8.51878 9.98629 8.25394 10.0454 7.98541 10.0507C7.71688 10.056 7.44992 10.0074 7.20049 9.90755C6.95106 9.80771 6.72426 9.65869 6.53279 9.46722C6.34132 9.27575 6.1923 9.04895 6.09246 8.79952C5.99261 8.55009 5.94406 8.28313 5.94934 8.0146C5.95461 7.74607 6.01372 7.48123 6.12315 7.2359C6.23257 6.99057 6.39018 6.76977 6.58667 6.58667M11.96 11.96C10.8204 12.8287 9.43275 13.3099 8.00001 13.3333C3.33334 13.3333 1.33334 8.00001 1.33334 8.00001C2.16289 6.45462 3.31824 5.10441 4.72001 4.04001L11.96 11.96ZM6.60001 2.82667C7.05889 2.71926 7.52871 2.66556 8.00001 2.66667C12.6667 2.66667 14.6667 8.00001 14.6667 8.00001C14.2608 8.75708 13.7637 9.4698 13.1867 10.12L6.60001 2.82667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1.33334 1.33334L14.6667 14.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="assets-action-buttons">
            <button className="assets-action-btn assets-action-btn-primary" onClick={() => navigate('/deposit')}>
              <div className="assets-action-icon assets-action-icon-filled">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.16667V15.8333M4.16667 10H15.8333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span>Deposit</span>
            </button>
            <button className="assets-action-btn" onClick={() => navigate('/withdraw')}>
              <div className="assets-action-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3.33334V12.6667M8 12.6667L12.6667 8.00001M8 12.6667L3.33334 8.00001" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span>Withdraw</span>
            </button>
            <button className="assets-action-btn" onClick={() => navigate('/swap')}>
              <div className="assets-action-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12.6667 5.33334L3.33334 5.33334M3.33334 5.33334L6.00001 2.66667M3.33334 5.33334L6.00001 8.00001M3.33334 10.6667H12.6667M12.6667 10.6667L10 8.00001M12.6667 10.6667L10 13.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span>Swap</span>
            </button>
            <button className="assets-action-btn" onClick={() => navigate('/p2p-buy-list')}>
              <div className="assets-action-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="5" cy="5" r="2" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="11" cy="11" r="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6.5 6.5L9.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>P2P</span>
            </button>
          </div>
        </div>

        {/* Asset Distribution Section */}
        <h2 className="assets-distribution-title">Asset Distribution</h2>

        <div className="assets-distribution-card">
          <div className="assets-distribution-content">
            {/* Donut Chart */}
            <div className="assets-donut-chart">
              <svg viewBox="0 0 100 100" className="assets-donut-svg">
                {donutSegments.map((segment, index) => (
                  <circle
                    key={segment.symbol}
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke={segment.color}
                    strokeWidth="10"
                    strokeDasharray={segment.strokeDasharray}
                    strokeDashoffset={segment.strokeDashoffset}
                    transform="rotate(-90 50 50)"
                    className="assets-donut-segment"
                  />
                ))}
              </svg>
            </div>

            {/* Legend */}
            <div className="assets-distribution-legend">
              {assetDistribution.map((asset) => (
                <div key={asset.symbol} className="assets-legend-item">
                  <div
                    className="assets-legend-dot"
                    style={{ backgroundColor: asset.color }}
                  />
                  <span className="assets-legend-symbol">{asset.symbol}</span>
                  <span className="assets-legend-percentage">{asset.percentage.toFixed(2)} %</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="assets-nav">
        <button
          className={`nav-item ${activeNav === 'home' ? 'active' : ''}`}
          onClick={() => navigate('/dashboard')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Home</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'markets' ? 'active' : ''}`}
          onClick={() => navigate('/markets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M22 12L18 16L14 12L10 16L6 12L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 6L18 10L14 6L10 10L6 6L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Markets</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'trade' ? 'active' : ''}`}
          onClick={() => {
            setActiveNav('trade');
            setTradeModalOpen(true);
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Trade</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'history' ? 'active' : ''}`}
          onClick={() => navigate('/history-deposit')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>History</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'assets' ? 'active' : ''}`}
          onClick={() => setActiveNav('assets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Assets</span>
        </button>
      </nav>

      {/* Trade Modal */}
      {tradeModalOpen && (
        <div className="trade-modal-overlay" onClick={() => setTradeModalOpen(false)}>
          <div className="trade-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              className="trade-modal-close"
              onClick={() => setTradeModalOpen(false)}
              aria-label="Close modal"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className="trade-modal-option" onClick={() => navigate('/p2p-buy-list')}>
              <div className="trade-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="trade-modal-option-content">
                <h3 className="trade-modal-option-title">P2P</h3>
                <p className="trade-modal-option-description">
                  Unlock financial freedom through P2P crypto trading: connect directly, trade efficiently, and decentralize your assets.
                </p>
              </div>
              <svg className="trade-modal-option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className="trade-modal-option">
              <div className="trade-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="trade-modal-option-content">
                <h3 className="trade-modal-option-title">Buy Crypto</h3>
                <p className="trade-modal-option-description">
                  Maximize crypto gains: Buy low, Sell high. Navigate digital markets with precision for optimal profits.
                </p>
              </div>
              <svg className="trade-modal-option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;
